import { tasksService } from './tasks-service.js';

/**
 * Integration service between Tasks and Big 3 planning
 * Provides smart suggestions from task list for Big 3 planning
 */
export const taskBig3Integration = {
  
  /**
   * Get task suggestions for Big 3 planning based on energy level and time of day
   */
  async getTaskSuggestionsForBig3(userId, timeSlot, currentEnergy = 'medium') {
    try {
      // Get pending tasks
      const tasks = await tasksService.getTasks(userId, { 
        status: 'pending',
        includeArchived: false 
      });
      
      // Filter and score tasks based on time slot and energy
      const suggestions = tasks
        .filter(task => {
          // Filter out tasks that don't match current context
          if (task.status !== 'pending') return false;
          
          // Filter out tasks marked for Big3 exclusion
          if (task.big3_ready === false) return false;
          
          // Include tasks due today or overdue for any time slot
          if (task.due_date) {
            const taskDate = new Date(task.due_date + 'T00:00:00');
            const today = new Date();
            if (taskDate <= today) return true;
          }
          
          // Include tasks that match energy level
          if (task.energy_level) {
            return this.energyMatches(task.energy_level, currentEnergy, timeSlot);
          }
          
          // Include unassigned tasks (no energy level)
          return true;
        })
        .map(task => ({
          ...task,
          suggestionScore: this.calculateSuggestionScore(task, timeSlot, currentEnergy),
          suggestionReason: this.getSuggestionReason(task, timeSlot, currentEnergy)
        }))
        .sort((a, b) => b.suggestionScore - a.suggestionScore)
        .slice(0, 5); // Top 5 suggestions
      
      return suggestions;
      
    } catch (error) {
      console.error('Error getting task suggestions:', error);
      return [];
    }
  },
  
  /**
   * Check if task energy level matches current energy and time slot
   */
  energyMatches(taskEnergy, currentEnergy, timeSlot) {
    // Intention 1: favor high/medium energy tasks
    if (timeSlot === 'intention1') {
      return taskEnergy === 'high' || taskEnergy === 'medium';
    }
    
    // Intention 2: any energy level works
    if (timeSlot === 'intention2') {
      return true;
    }
    
    // Intention 3: favor low/medium energy tasks
    if (timeSlot === 'intention3') {
      return taskEnergy === 'low' || taskEnergy === 'medium';
    }
    
    return true;
  },
  
  /**
   * Calculate suggestion score for ranking tasks
   */
  calculateSuggestionScore(task, timeSlot, currentEnergy) {
    let score = 0;
    
    // Priority scoring
    if (task.priority === 'high') score += 30;
    else if (task.priority === 'medium') score += 20;
    else score += 10;
    
    // Due date scoring
    if (task.due_date) {
      const taskDate = new Date(task.due_date + 'T00:00:00');
      const today = new Date();
      const daysDiff = Math.ceil((taskDate - today) / (1000 * 60 * 60 * 24));
      
      if (daysDiff < 0) score += 50; // Overdue
      else if (daysDiff === 0) score += 40; // Due today
      else if (daysDiff === 1) score += 20; // Due tomorrow
      else if (daysDiff <= 3) score += 10; // Due this week
    }
    
    // Energy level scoring
    if (task.energy_level) {
      if (this.energyMatches(task.energy_level, currentEnergy, timeSlot)) {
        score += 25;
      } else {
        score -= 10; // Penalty for energy mismatch
      }
    } else {
      score += 5; // Small bonus for flexible tasks
    }
    
    // Task type scoring
    if (task.task_type === 'reminder') score += 15; // Reminders are important
    if (task.task_type === 'manual') score += 10; // Manually created tasks
    if (task.task_type === 'recommendation') score += 20; // Tasks from recommendations
    
    // Special category scoring
    if (task.category === 'selected_for_big3') score += 35; // Boost for manually selected Big3 items
    
    return score;
  },
  
  /**
   * Get human-readable reason for suggestion
   */
  getSuggestionReason(task, timeSlot, currentEnergy) {
    const reasons = [];
    
    if (task.due_date) {
      const taskDate = new Date(task.due_date + 'T00:00:00');
      const today = new Date();
      const daysDiff = Math.ceil((taskDate - today) / (1000 * 60 * 60 * 24));
      
      if (daysDiff < 0) reasons.push('Overdue');
      else if (daysDiff === 0) reasons.push('Due today');
      else if (daysDiff === 1) reasons.push('Due tomorrow');
    }
    
    if (task.priority === 'high') reasons.push('High priority');
    
    if (task.energy_level) {
      if (this.energyMatches(task.energy_level, currentEnergy, timeSlot)) {
        reasons.push(`Good ${timeSlot} energy match`);
      }
    }
    
    if (task.task_type === 'reminder') {
      const senderName = task.source_message?.sender_name;
      reasons.push(senderName ? `From ${senderName}` : 'From reminder');
    }
    
    if (task.category === 'selected_for_big3') {
      reasons.push('Selected for Big3');
    }
    
    if (task.task_type === 'recommendation') {
      reasons.push('From recommendations');
    }
    
    return reasons.length > 0 ? reasons.join(' • ') : 'Available task';
  },
  
  /**
   * Move a task to Big 3 planning (marks as planned, removes from regular task list)
   */
  async moveTaskToBig3(taskId, userId, targetDate = null) {
    try {
      const task = await tasksService.getTasks(userId, { includeArchived: false })
        .then(tasks => tasks.find(t => t.id === taskId));
      
      if (!task) throw new Error('Task not found');
      
      // If task has no due date or target date is specified, move it to planned status
      if (!task.due_date || targetDate) {
        await tasksService.updateTask(taskId, {
          status: 'planned_for_big3',
          category: 'big3-planned',
          planned_date: targetDate || new Date().toISOString().split('T')[0]
        }, userId);
        
        return {
          success: true,
          taskData: {
            id: taskId,
            title: task.title,
            originalTask: task
          }
        };
      } else {
        // Task has a due date - it should remain visible on due date
        await tasksService.updateTask(taskId, {
          category: 'big3-available',
          big3_ready: true
        }, userId);
        
        return {
          success: true,
          taskData: {
            id: taskId,
            title: task.title,
            originalTask: task,
            willReturnOnDate: task.due_date
          }
        };
      }
    } catch (error) {
      console.error('Error moving task to Big 3:', error);
      throw error;
    }
  },

  /**
   * Get tasks available for Big 3 planning on a specific date
   */
  async getTasksForBig3Date(userId, date) {
    try {
      const tasks = await tasksService.getTasks(userId, { includeArchived: false });
      
      return tasks.filter(task => {
        // Include tasks due on this date
        if (task.due_date === date && task.status === 'pending') return true;
        
        // Include tasks marked as Big 3 ready without due dates
        if (task.big3_ready && !task.due_date && task.status === 'pending') return true;
        
        // Include overdue tasks
        if (task.due_date && task.due_date < date && task.status === 'pending') return true;
        
        return false;
      });
    } catch (error) {
      console.error('Error getting tasks for Big 3 date:', error);
      return [];
    }
  },

  /**
   * Restore a task from Big 3 planning back to regular task list
   */
  async restoreTaskFromBig3(taskId, userId) {
    try {
      await tasksService.updateTask(taskId, {
        status: 'pending',
        category: null,
        planned_date: null,
        big3_ready: null
      }, userId);
      
      return { success: true };
    } catch (error) {
      console.error('Error restoring task from Big 3:', error);
      throw error;
    }
  },
  
  /**
   * Get completed Big 3 tasks for the day (for Big 3 History)
   */
  async getBig3CompletedTasks(userId, date) {
    try {
      const tasks = await tasksService.getTasks(userId, {
        status: 'completed',
        includeArchived: false
      });
      
      return tasks.filter(task => {
        // Check if it's a Big 3 related task
        if (!task.category?.startsWith('big3-')) return false;
        
        // Check if completed on the specified date
        if (task.completed_at) {
          const completedDate = new Date(task.completed_at).toISOString().split('T')[0];
          return completedDate === date;
        }
        
        return false;
      });
      
    } catch (error) {
      console.error('Error getting Big 3 completed tasks:', error);
      return [];
    }
  },
  
  /**
   * Mark a task as completed when Big 3 item is checked off
   */
  async completeBig3Task(taskId, userId) {
    try {
      await tasksService.updateTask(taskId, {
        status: 'completed'
      }, userId);
    } catch (error) {
      console.error('Error completing Big 3 task:', error);
    }
  }
};